export const FILTER_PREFERENCE_STORAGE_KEY = 'filter_page_preferences_v1';

export const FILTER_PREFERENCE_PAGES = [
  {
    id: 'bus-log-signals',
    title: 'Bus Log Signals',
    route: '/bus_log',
    storageKey: 'visualize_signals_preferences',
    storageMode: 'signal-list',
    apiBaseUrl: null,
    signalAutocompleteEndpoint: '/api/v1/buslog/signals/autocomplete',
    networkAutocompleteEndpoint: '/api/v1/buslog/networks/autocomplete',
    fields: [],
  },
  {
    id: 'timeline',
    title: 'Timeline',
    route: '/timeline',
    storageKey: 'timeline_filter_preferences',
    apiBaseUrl: '/api/filters',
    fields: [
      { key: 'vin', label: 'VIN', singleSelect: true },
      { key: 'mac_id', label: 'MAC ID', singleSelect: true },
    ],
  },
  {
    id: 'files',
    title: 'Files',
    route: '/files',
    storageKey: 'logger_filter_preferences',
    apiBaseUrl: '/api/filters',
    fields: [
      { key: 'vin', label: 'VIN' },
      { key: 'mac_id', label: 'MAC ID' },
      { key: 'category', label: 'Category' },
      { key: 'file_type', label: 'File Type' },
    ],
  },
  {
    id: 'dit',
    title: 'DIT',
    route: '/dit',
    storageKey: 'dit_filter_preferences',
    apiBaseUrl: '/api/dit',
    fields: [
      { key: 'vin', label: 'VIN' },
      { key: 'mac_id', label: 'MAC ID' },
      { key: 'vescom', label: 'Vescom' },
      { key: 'year', label: 'Year' },
      { key: 'program', label: 'Program' },
    ],
  },
  {
    id: 'dtc-log',
    title: 'DTC Log',
    route: '/dtc_log',
    storageKey: 'dtc_filter_preferences',
    apiBaseUrl: '/api/dtc_logs',
    fields: [
      { key: 'vin', label: 'VIN' },
      { key: 'mac_id', label: 'MAC ID' },
      { key: 'module', label: 'Module' },
      { key: 'response', label: 'Response' },
      { key: 'dtc_type', label: 'DTC Type' },
      { key: 'status_new', label: 'Status New' },
      { key: 'preexisting', label: 'Preexisting' },
    ],
  },
  {
    id: 'part-number-pull',
    title: 'Part Number Pull',
    route: '/part_number_pull',
    storageKey: 'pnp_filter_preferences',
    apiBaseUrl: '/api/part_number_pulls',
    fields: [
      { key: 'vin', label: 'VIN' },
      { key: 'module', label: 'Module' },
      { key: 'diagnostic_id', label: 'Diagnostic ID' },
      { key: 'did', label: 'DID' },
    ],
  },
  {
    id: 'test-result-ingestion',
    title: 'Test Result Ingestion',
    route: '/test_result_ingestion',
    storageKey: 'test_result_ingestion_filter_preferences',
    apiBaseUrl: '/api/test_result_ingestions',
    fields: [
      { key: 'vin', label: 'VIN' },
      { key: 'mac_id', label: 'MAC ID' },
      { key: 'suite_name', label: 'Suite Name' },
      { key: 'status', label: 'Status' },
      { key: 'reason_type', label: 'Reason Type' },
    ],
  },
  {
    id: 'event',
    title: 'Event',
    route: '/event',
    storageKey: 'event_filter_preferences',
    apiBaseUrl: '/api/events',
    fields: [
      { key: 'vin', label: 'VIN' },
      { key: 'mac_id', label: 'MAC ID' },
      { key: 'trigger_type', label: 'Trigger Type' },
      { key: 'summary', label: 'Summary' },
      { key: 'trip', label: 'Trip' },
    ],
  },
];

const getDefaultPagePreference = (page) => ({
  enabled: true,
  selectedFields: page.fields.map((field) => field.key),
});

const encodeSignalEntry = ({ signalName, network = '' }) =>
  network ? `${signalName}|${network}` : signalName;

export const decodeSavedSignalEntries = (encodedValue = '') => {
  if (!encodedValue || typeof encodedValue !== 'string') {
    return [];
  }

  return encodedValue
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [signalName, network = ''] = entry.split('|');
      return {
        signalName: signalName?.trim() || '',
        network: network?.trim() || '',
      };
    })
    .filter((entry) => entry.signalName);
};

export const encodeSavedSignalEntries = (entries = []) => {
  return entries
    .map((entry) => ({
      signalName: entry?.signalName?.trim() || '',
      network: entry?.network?.trim() || '',
    }))
    .filter((entry) => entry.signalName)
    .map(encodeSignalEntry)
    .join(',');
};

const sanitizePagePreference = (page, pagePreference = {}) => {
  const allowedFields = new Set(page.fields.map((field) => field.key));
  const selectedFields = Array.isArray(pagePreference.selectedFields)
    ? pagePreference.selectedFields.filter((field) => allowedFields.has(field))
    : page.fields.map((field) => field.key);

  return {
    enabled: typeof pagePreference.enabled === 'boolean' ? pagePreference.enabled : true,
    selectedFields,
  };
};

const sanitizePreferenceConfig = (config = {}) => {
  return FILTER_PREFERENCE_PAGES.reduce((accumulator, page) => {
    accumulator[page.id] = sanitizePagePreference(page, config[page.id]);
    return accumulator;
  }, {});
};

export const getDefaultFilterPreferenceConfig = () => {
  return FILTER_PREFERENCE_PAGES.reduce((accumulator, page) => {
    accumulator[page.id] = getDefaultPagePreference(page);
    return accumulator;
  }, {});
};

export const readFilterPreferenceConfig = () => {
  try {
    const savedConfig = localStorage.getItem(FILTER_PREFERENCE_STORAGE_KEY);
    if (!savedConfig) {
      return getDefaultFilterPreferenceConfig();
    }

    return sanitizePreferenceConfig(JSON.parse(savedConfig));
  } catch (error) {
    return getDefaultFilterPreferenceConfig();
  }
};

export const getFilterPreferencePageById = (pageId) => {
  return FILTER_PREFERENCE_PAGES.find((page) => page.id === pageId) || null;
};

export const getFilterPreferencePageByStorageKey = (storageKey) => {
  return FILTER_PREFERENCE_PAGES.find((page) => page.storageKey === storageKey) || null;
};

export const getFilterPreferenceSelection = (storageKey, defaultFields = {}) => {
  const fallbackFields = Object.keys(defaultFields || {});
  const page = getFilterPreferencePageByStorageKey(storageKey);

  if (!page) {
    return {
      enabled: true,
      selectedFields: fallbackFields,
    };
  }

  const config = readFilterPreferenceConfig();
  const pagePreference = config[page.id] || getDefaultPagePreference(page);
  const allowedFields = fallbackFields.length > 0 ? fallbackFields : page.fields.map((field) => field.key);

  return {
    enabled: pagePreference.enabled,
    selectedFields: pagePreference.selectedFields.filter((field) => allowedFields.includes(field)),
  };
};

export const readStoredSignalEntries = (storageKey) => {
  try {
    return decodeSavedSignalEntries(localStorage.getItem(storageKey) || '');
  } catch (error) {
    return [];
  }
};

export const writeStoredSignalEntries = (storageKey, entries = []) => {
  const encodedValue = encodeSavedSignalEntries(entries);

  if (encodedValue) {
    localStorage.setItem(storageKey, encodedValue);
  } else {
    localStorage.removeItem(storageKey);
  }

  window.dispatchEvent(new Event('localStorageChange'));
  return encodedValue;
};

export const readStoredFilters = (storageKey, allowedFields = []) => {
  const emptyState = allowedFields.reduce((accumulator, field) => {
    accumulator[field] = [];
    return accumulator;
  }, {});

  try {
    const savedFilters = localStorage.getItem(storageKey);
    if (!savedFilters) {
      return emptyState;
    }

    const parsedFilters = JSON.parse(savedFilters);
    return allowedFields.reduce((accumulator, field) => {
      accumulator[field] = Array.isArray(parsedFilters?.[field]) ? parsedFilters[field] : [];
      return accumulator;
    }, { ...emptyState });
  } catch (error) {
    return emptyState;
  }
};

export const writeStoredFilters = (storageKey, filters, allowedFields = []) => {
  const normalizedFilters = allowedFields.reduce((accumulator, field) => {
    accumulator[field] = Array.isArray(filters?.[field]) ? filters[field] : [];
    return accumulator;
  }, {});

  const hasData = Object.values(normalizedFilters).some(
    (value) => Array.isArray(value) && value.length > 0
  );

  if (hasData) {
    localStorage.setItem(storageKey, JSON.stringify(normalizedFilters));
  } else {
    localStorage.removeItem(storageKey);
  }

  window.dispatchEvent(new Event('localStorageChange'));
  return normalizedFilters;
};

export const syncStoredFiltersWithPreferenceConfig = (config = readFilterPreferenceConfig()) => {
  const normalizedConfig = sanitizePreferenceConfig(config);

  FILTER_PREFERENCE_PAGES.forEach((page) => {
    const pagePreference = normalizedConfig[page.id] || getDefaultPagePreference(page);

    if (page.storageMode === 'signal-list') {
      if (!pagePreference.enabled) {
        localStorage.removeItem(page.storageKey);
      }
      return;
    }

    const selectedFields = pagePreference.enabled ? pagePreference.selectedFields : [];

    if (selectedFields.length === 0) {
      localStorage.removeItem(page.storageKey);
      return;
    }

    const savedFilters = localStorage.getItem(page.storageKey);
    if (!savedFilters) {
      return;
    }

    try {
      const parsedFilters = JSON.parse(savedFilters);
      writeStoredFilters(page.storageKey, parsedFilters, selectedFields);
    } catch (error) {
      localStorage.removeItem(page.storageKey);
    }
  });
};

export const writeFilterPreferenceConfig = (config) => {
  const normalizedConfig = sanitizePreferenceConfig(config);
  localStorage.setItem(FILTER_PREFERENCE_STORAGE_KEY, JSON.stringify(normalizedConfig));
  syncStoredFiltersWithPreferenceConfig(normalizedConfig);
  window.dispatchEvent(new Event('localStorageChange'));
  return normalizedConfig;
};